import React from 'react';
import { Form } from 'antd';
import { booleanToSelectValue } from 'utils/booleanToSelectValue';
import { useStore } from 'routes/inventorize_meter/useStore';
import { ElectricityDetails } from 'components/Form/meter_details/electricity/ElectricityDetails';
import { ElectricityMeterDetailField } from 'components/Form/meter_details/electricity/constants';
import { Phase } from '@enmon/commons';
import { MeterDetailElectricity } from '@enmon/commons/lib/enmon';
import { Merge } from 'type-fest';
import { FormItemProps } from '../../../../../components/Form/FormItem';

export type MeterElectricityAdditionalDetails = Merge<
  MeterDetailElectricity,
  {
    width: { value: MeterDetailElectricity['width']; unit?: string } | null;
    isDirect: number | null;
  }
>;

export type MeterElectricityDetailsProps = MeterElectricityAdditionalDetails & {
  onFormItemReset: FormItemProps['onResetField'];
  meterId?: number;
};

/* eslint-disable camelcase */
export const ElectricityAdditionalFields: React.VFC<MeterElectricityDetailsProps> = props => {
  const {
    deviceCategory,
    isDirect,
    measuringCurrentTransformer,
    mounting,
    width,
    tariffs,
    iMin,
    iMax,
    iRef,
    phases: storePhases,
    measurementCategory,
    measurementType,
    circuitBreakerSize,
    inputSpec,
    onFormItemReset,
    meterId,
  } = props;

  const form = Form.useFormInstance();
  const phases = Form.useWatch<Phase | undefined>(['detail', 'phases'], form) ?? storePhases;
  const { setDetailStepValues, detail, newMeter } = useStore();

  // Resets phases and circuit breaker size when phases are reset
  const handlePhaseReset = () => {
    const emptyPhaseAndCircuitBreaker = {
      detail: { ...detail, phases: null, circuitBreakerSize: null } as MeterElectricityAdditionalDetails,
    };
    form.setFieldsValue(emptyPhaseAndCircuitBreaker);

    setDetailStepValues(emptyPhaseAndCircuitBreaker);
  };

  return (
    <ElectricityDetails
      itemsConfig={{
        [ElectricityMeterDetailField.DeviceCategory]: {
          formItemProps: { initialValue: deviceCategory, isSemiRequired: !meterId, onResetField: onFormItemReset },
        },
        [ElectricityMeterDetailField.MeasurementType]: {
          formItemProps: {
            initialValue: measurementType,
            isSemiRequired: !meterId,
            onResetField: onFormItemReset,
          },
        },
        [ElectricityMeterDetailField.MeasurementCategory]: {
          formItemProps: {
            initialValue: measurementCategory,
            isSemiRequired: !meterId,
            onResetField: onFormItemReset,
          },
        },
        ...(!newMeter && {
          [ElectricityMeterDetailField.Direct]: {
            formItemProps: {
              initialValue: booleanToSelectValue(isDirect),
              isSemiRequired: !meterId,
              onResetField: onFormItemReset,
            },
          },
          ...(!isDirect && {
            [ElectricityMeterDetailField.MCT]: {
              formItemProps: {
                initialValue: measuringCurrentTransformer,
                isSemiRequired: !meterId,
                onResetField: onFormItemReset,
              },
            },
          }),
        }),
        [ElectricityMeterDetailField.Phases]: {
          formItemProps: { initialValue: phases, isSemiRequired: !meterId, onResetField: handlePhaseReset },
        },
        [ElectricityMeterDetailField.Mounting]: {
          formItemProps: { initialValue: mounting, isSemiRequired: !meterId, onResetField: onFormItemReset },
        },
        [ElectricityMeterDetailField.Width]: {
          formItemProps: { initialValue: width?.value },
          additionalConfig: { widthUnitProps: { initialValue: width?.unit ?? 'mm' } },
        },
        [ElectricityMeterDetailField.Tariffs]: {
          formItemProps: { initialValue: tariffs, isSemiRequired: !meterId, onResetField: onFormItemReset },
        },
        ...(!newMeter && {
          [ElectricityMeterDetailField.IMin]: {
            formItemProps: { initialValue: iMin, isSemiRequired: !meterId, onResetField: onFormItemReset },
          },
          [ElectricityMeterDetailField.IRef]: {
            formItemProps: { initialValue: iRef, isSemiRequired: !meterId, onResetField: onFormItemReset },
          },
          [ElectricityMeterDetailField.IMax]: {
            formItemProps: { initialValue: iMax, isSemiRequired: !meterId, onResetField: onFormItemReset },
          },
        }),
        [ElectricityMeterDetailField.CircuitBreakerSize]: {
          formItemProps: {
            initialValue: circuitBreakerSize,
            isSemiRequired: !meterId,
            onResetField: onFormItemReset,
          },
          additionalConfig: { phases },
        },
        [ElectricityMeterDetailField.InputSpec]: {
          formItemProps: { initialValue: inputSpec },
        },
      }}
    />
  );
};
